/**
 * 「开始做」那张卡（设计稿 goal）。
 *
 * # 它补的是整条闭环里唯一断掉的那一步
 *
 * 服务端这条链一直是通的：
 *
 *   人说一句 → Lulu 算出计划（RememberPlan 落 checkpoint）
 *     → 「安排开发」→ 建 DevelopmentTask → BindTask → openBoard
 *     → lulu_work_items → outbox → adapter.Start → 工作板有东西了
 *
 * 而 `/lulu` 上**没有任何一个入口能触发第三步**。唯一按得到「安排开发」的
 * 地方是 `screens/project/modules/conversation.tsx`（另一套聊天）。
 * 更说明问题的是：`/lulu` 上**有「撤回」没有「安排」**——
 * 它能取消一条它自己开不出来的任务。
 *
 * # 为什么这一档要一个纯函数
 *
 * 「现在能不能点开始做」有五个来源（计划、项目绑定、已安排过、归档、
 * 请求在飞），而它们各自的错法都是静默的：
 *
 *   - 计划还没到就画这张卡 → 一张空卡，读起来像加载坏了
 *   - 已经安排过还让点 → 用户去撞服务端那句 ErrAlreadyScheduled，
 *     而那堵墙我们早就知道
 *   - 通用会话上画这张卡 → 点下去必然 409（计划是按项目算的）
 *
 * 放在组件里，这五条只有「跑起来看看」这一种验法。
 */

/** 服务端 `GET /conversations/{id}/plan` 回的形状。 */
export interface WirePlan {
  readonly objective: string
  readonly acceptance: readonly string[]
  /** 非空 = 这一轮已经安排过了。 */
  readonly task_id: string
}

export type PlanCardState =
  /** 不画这张卡。`why` 只用于用例与排查，不上屏。 */
  | { readonly kind: 'hidden'; readonly why: HiddenReason }
  /** 画卡，主按钮能点。 */
  | { readonly kind: 'ready' }
  /** 画卡，主按钮按不动，并说清这一轮已经安排下去了。 */
  | { readonly kind: 'scheduled'; readonly taskId: string }

export type HiddenReason =
  /** 还没读到（请求在飞 / 读失败）。**不画空卡。** */
  | 'unknown'
  /** 通用会话：计划是按项目算的，这条会话等多久都不会有。 */
  | 'no_project'
  /** Lulu 还没回。正常状态，等就是了。 */
  | 'no_plan'
  /** 归档的会话不接受写。 */
  | 'archived'
  /**
   * 这件事早就安排下去了（服务端刷回来的 task_id）。
   * 卡收起来——**它是一条已经回答完的通知，不是一个等他回答的问题**，
   * 进度在工作板上。
   */
  | 'already_scheduled'

export interface PlanCardInput {
  /** `undefined` = 还没读到。`null` = 读失败。两者都不画。 */
  readonly plan: WirePlan | null | undefined
  /** 这条会话挂的项目。`null` = 通用会话。 */
  readonly projectId: string | null
  readonly archived: boolean
  /**
   * 这一次点「开始做」刚拿到的 task_id（`POST /schedule` 的响应）。
   *
   * # 为什么不能只等 `plan.task_id` 刷回来
   *
   * 2026-08-30 生产实测：点完之后服务端确实建出了任务，而卡片一直停在
   * `ready`、按钮一直可点，**直到手动刷新页面**。那几秒里用户可以再点一次，
   * 第二下撞服务端的 `ErrAlreadyScheduled`——正是 plan-card.tsx 那句注释
   * 说不该让他撞的墙。
   *
   * `startWork` 里已经 `await goalPlan.refetch()` 了，它不够：那是一次网络
   * 往返，而往返期间界面必须已经是「安排过了」。
   *
   * **用它不是编一个状态**：这个 id 是 `POST /schedule` 的响应刚给的，
   * 它比任何一次后续查询都更权威——那次写入就是它做的。
   */
  readonly justScheduledTaskId?: string | null
}

/**
 * 现在该画什么。
 *
 * 顺序不是随手排的：**先答「该不该有这张卡」，再答「卡上是什么」。**
 * 反过来的话，一条通用会话在计划读不到的那几秒会走到 `unknown`，
 * 而它真正的答案是 `no_project`——两句话让用户做的事不同
 * （等一下 / 去绑一个项目）。
 */
export function planCardState(input: PlanCardInput): PlanCardState {
  if (input.archived) return { kind: 'hidden', why: 'archived' }
  if (input.projectId === null) return { kind: 'hidden', why: 'no_project' }
  if (input.plan === undefined || input.plan === null) {
    return { kind: 'hidden', why: 'unknown' }
  }
  // **objective 与 acceptance 缺任一条都不画。**
  //
  // 服务端那侧 `taskFor` 正是拿这两样判 ErrNothingConfirmed——
  // 少了任一条，「开始做」点下去必然失败。画一张点了就报错的卡，
  // 比不画糟得多。
  if (input.plan.objective.trim() === '' || input.plan.acceptance.length === 0) {
    return { kind: 'hidden', why: 'no_plan' }
  }
  // **服务端说的优先。** 两个都有时用服务端那个：本地这个只是
  // 「我刚提交的那次」，而服务端那个是「这一轮最终绑上的是哪条」——
  // 重放或并发时它们可能不是同一条，那时可信的是库里那份。
  // # 「已经安排下去了」只是一句瞬时的回执，不该永久占位
  //
  // 2026-09-08 arron 报的：这块应该是触发才出现的，不是一直这样。
  // 原来只要 plan.task_id 一落库，卡就永远停在 scheduled——一条已经完成
  // 的通知占着输入框上方那一整块，而进度右边工作板上本来就有。
  //
  // 两态要分开对待：
  //   ready      是一个**等他回答**的问题（对不对？开始做？），留着是对的
  //   scheduled  是一条**已经回答完**的通知，只在刚点完那几秒里说一声
  //
  // 所以 scheduled 只由 justScheduledTaskId 驱动（那是这一次点击刚拿到的
  // id，index.tsx 里几秒后清掉）；服务端刷回来的 plan.task_id 说明这件事
  // 早就办完了——**卡收起来，工作板是它该在的地方**。
  if ((input.justScheduledTaskId ?? '') !== '') {
    return { kind: 'scheduled', taskId: input.justScheduledTaskId as string }
  }
  if (input.plan.task_id !== '') {
    return { kind: 'hidden', why: 'already_scheduled' }
  }
  return { kind: 'ready' }
}

/**
 * 这张卡上出哪几条出口。
 *
 * 设计稿 goal 写的是三条：`['开始做','p'], '我改一下目标', '换个项目'`。
 * **只出前两条。**
 *
 * 「换个项目」拿掉，不是漏了：绑定是单向的（服务端再绑回 409），
 * 而这一点已经有一句文案在说（`lulu_workbench.bind.one_way`
 * 「绑了就换不了」）。摆一个点下去必然失败的出口，
 * 与「画一张点了就报错的卡」是同一件事——**而这一次它还与我们自己
 * 已经说过的那句话直接矛盾**。
 *
 * 「我改一下目标」不发任何请求：它就是把光标放回输入框，
 * 让他再说一句。改目标的办法本来就是再说一句话，不是一个表单。
 */
export const PLAN_EXITS = Object.freeze(['start', 'revise'] as const)

export type PlanExit = (typeof PLAN_EXITS)[number]
